import React, { useEffect, useState } from 'react';
import { usePollJob } from '../hooks/usePollJob';
import type { RttsGateFields, RttsJobDetail } from '../types';
import { ReelPosterThumb } from './BrandedReelFallback';
import ReelPlayer from './ReelPlayer';
import ShareCard from './ShareCard';

export type InlineReelPanelProps = {
  leagueId: string;
  gates: RttsGateFields;
  /** Start expanded (e.g. after a fresh generate delivery). */
  initiallyExpanded?: boolean;
  className?: string;
};

function existingReelToJob(
  reel: NonNullable<RttsGateFields['existing_reel']>
): RttsJobDetail {
  return {
    id: reel.job_id,
    status: reel.status || 'delivered',
    signed_url: reel.signed_url ?? null,
    expires_at: reel.expires_at ?? null,
    reel_duration_sec: reel.reel_duration_sec ?? null,
    error_code: null,
    share_thumbnail_url: reel.poster_url ?? null,
  };
}

/** Prefer reel-native poster; never hand a bare archive thumb as the only surface. */
export function resolveReelPosterUrl(gates: RttsGateFields): string | null {
  const reelPoster = gates.existing_reel?.poster_url?.trim();
  if (reelPoster) return reelPoster;
  return null;
}

/**
 * Thumbnail → expand inline ReelPlayer (no full-page navigation).
 * Shared by the RTTS tab and Archive panel so behavior stays identical.
 */
const InlineReelPanel: React.FC<InlineReelPanelProps> = ({
  leagueId,
  gates,
  initiallyExpanded = false,
  className,
}) => {
  const reel = gates.existing_reel;
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const posterUrl = resolveReelPosterUrl(gates);

  useEffect(() => {
    if (initiallyExpanded) setExpanded(true);
  }, [initiallyExpanded]);

  if (!reel) return null;

  if (!expanded) {
    return (
      <div
        className={['w-full max-w-full', className].filter(Boolean).join(' ')}
        data-testid="rtts-inline-reel"
      >
        <button
          type="button"
          className="group relative block w-full max-w-full overflow-hidden rounded-lg border border-secondary-700 bg-secondary-950 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          data-testid="rtts-inline-reel-thumb"
          aria-label="Watch reel"
          onClick={() => setExpanded(true)}
        >
          <div className="relative aspect-video w-full">
            <ReelPosterThumb posterUrl={posterUrl} gates={gates} />
            <span
              className="absolute inset-0 flex items-center justify-center bg-black/35 transition group-hover:bg-black/45"
              aria-hidden
            >
              <span className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-black/70 px-4 text-xs font-medium text-white">
                Watch reel
              </span>
            </span>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div
      className={['w-full max-w-full', className].filter(Boolean).join(' ')}
      data-testid="rtts-inline-reel-expanded"
    >
      <DeliveredReel
        leagueId={leagueId}
        jobId={reel.job_id}
        seedJob={existingReelToJob(reel)}
        titleFallback={gates.matchup}
        posterUrl={posterUrl}
      />
    </div>
  );
};

function DeliveredReel({
  leagueId,
  jobId,
  seedJob,
  titleFallback,
  posterUrl,
}: {
  leagueId: string;
  jobId: string;
  seedJob: RttsJobDetail | null;
  titleFallback: string;
  posterUrl: string | null;
}) {
  const { job, refresh } = usePollJob({
    leagueId,
    jobId,
    enabled: false,
  });

  useEffect(() => {
    if (!seedJob?.signed_url) {
      void refresh();
    }
  }, [seedJob, refresh]);

  const active = job || seedJob;
  if (!active) {
    return (
      <p className="text-xs text-secondary-400" data-testid="rtts-delivered-loading">
        Loading reel…
      </p>
    );
  }

  return (
    <ReelPlayer job={active} onRefreshUrl={refresh} posterUrl={posterUrl}>
      <ShareCard job={active} titleFallback={titleFallback} />
    </ReelPlayer>
  );
}

export default InlineReelPanel;
